// EditableText — the inline manual-edit affordance for a single text field.
//
// A small leaf that renders a text value read-only until the author activates
// it, then swaps to an inline textarea; on blur or Enter it commits the changed
// value UP via `onCommit`, on Escape it reverts. It is the MANUAL twin of an
// agent edit: the value it commits flows through the SAME `update_element_text`
// primitive (the host wires `onCommit` to its `updateElementText` port), never a
// bespoke UI-only mutation.
//
// It is the one Editor leaf that holds EPHEMERAL local state — the in-progress
// input buffer (`draft`) and whether it is editing — exactly as the design brief
// sanctions (like a composer's own text buffer). That state never escapes: the
// only thing that leaves is the committed string, and only when it differs from
// the incoming `value`. No I/O, no fetch, no domain knowledge; the display
// always reads the CURRENT `value` prop, so an external change (a successful
// commit, or a rollback) reflects immediately.
//
// Full-width box, both faces: display and edit share one block, `width:100%`
// box (see .css), so a long field WRAPS within its column and the swap never
// shifts layout or shrinks the box. Edit mode is an auto-growing `<textarea>`
// (`field-sizing:content`) — it grows to fit the text instead of scrolling a
// single line. Renderers place this box in a `flex:1; min-width:0` column beside
// its label (Badge / letter), so the text fills the width and never wraps under
// the label.
//
// A11y / design-system discipline:
//   • Keyboard-operable: the resting affordance is a native <button> (Enter/Space
//     to edit, for free); the textarea takes Enter (commit) / Escape (revert),
//     Shift+Enter inserts a newline.
//   • Named: `ariaLabel` names both the affordance and the textarea (a control is
//     never nameless) — and it carries the "editable" meaning for AT, so the
//     cue is not colour-alone (SC 1.4.1); the hover/focus tint is a redundant
//     visual channel, not the only one.
//   • Target size: a min-height floor keeps the hit target ≥24px.
//   • Borderless register: no resting edge; an edge is revealed by hover/focus
//     (the same discipline as Textarea), tokens only.

import type { ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import {
    dismissSlashTrigger,
    matchRule,
    nextSlashTrigger,
    type Rule,
    type RuleMatch,
    type SlashTriggerState,
} from "../inputRules.js";
import "./EditableText.css";

export type EditableTextProps = {
    readonly value: string;
    // Commit the new text UP. Called on blur / Enter, only when it CHANGED.
    readonly onCommit: (text: string) => void;
    // Names the affordance + textarea for AT, and carries the "editable" meaning.
    readonly ariaLabel: string;
    // Shown (dimmed) in the resting affordance when `value` is empty, and as the
    // textarea's placeholder while editing. Lets an empty optional field (e.g. an
    // unauthored MCQ feedback) still present a clickable "Add …" target instead
    // of an invisible empty box. Absent ⇒ an empty value renders as an empty box.
    readonly placeholder?: string;
    // Optional custom RESTING face. When present, the read-only affordance renders
    // `renderDisplay(value)` instead of the raw string — so a field whose source
    // is markdown (a prose block) keeps its parsed reading face at rest while
    // still editing the raw source in the textarea. The EDIT face is always the
    // plain textarea over `value` (you edit the source, not the rendered node).
    // Absent ⇒ the resting face is the plain string (the default for plain-text
    // fields: heading, objective, choice text, feedback).
    readonly renderDisplay?: (value: string) => ReactNode;
    readonly className?: string;
    // The markdown input-rule table to detect against WHILE typing (prose
    // blocks only — `# `/`## `/`### ` etc, inputRules.ts's MARKDOWN_RULES).
    // Absent ⇒ no live detection (the default for every non-prose field:
    // heading text, objective text, an MCQ stem/choice, feedback — none of
    // which are markdown-interpreted). This leaf runs the PURE `matchRule`
    // engine and reports the RAW match up; it never builds the resulting
    // element itself (`onRuleMatch`'s caller has the typed Element `matchRule`
    // needs `RuleContext` for — see ProseRenderer).
    readonly rules?: readonly Rule[];
    // A rule matched the draft WHILE typing. Absent ⇒ `rules` (if given) is
    // still detected against but never reported — effectively no-detection.
    readonly onRuleMatch?: (match: RuleMatch) => void;
    // The `/` slash-trigger's CURRENT state, CONTROLLED by the caller (the
    // same discipline as MaterialDocument's `slashMenu` prop) — this leaf
    // holds none of its own slash state. Absent ⇒ treated as "closed".
    readonly slashTriggerState?: SlashTriggerState;
    // Report the NEXT slash-trigger state after a keystroke, plus the
    // query typed after the leading `/` (empty string once the state is
    // anything but "open"/a bare "/"). Absent ⇒ no slash-trigger detection.
    readonly onSlashTrigger?: (state: SlashTriggerState, query: string) => void;
    // Move the OPEN slash menu's keyboard cursor (↑/↓) — reported UP because
    // this leaf holds no menu state; the host owns the active index and the
    // filtered rows (same discipline as `slashTriggerState`). Fired only while
    // `slashTriggerState === "open"`. Absent ⇒ arrows do native caret movement.
    readonly onSlashNavigate?: (direction: "up" | "down") => void;
    // Commit the OPEN slash menu's ACTIVE row (Enter) — the keyboard twin of the
    // menu's mouse click. The host picks `items[activeIndex]` and inserts. Fired
    // only while the menu is open; when it is, Enter never commits text or splits
    // a block (the menu pick takes precedence). Absent ⇒ Enter behaves normally.
    readonly onSlashCommitActive?: () => void;
    // The document-first "new block below" gesture: Enter pressed with the caret
    // at the END of the text and no open slash menu. Carries the just-typed text
    // so the host persists THIS block's edit and the sibling insert as ONE
    // sequenced chain (threading the returned content hash — no optimistic-lock
    // race between two independent edits). Wired only on top-level document
    // blocks (prose / heading / objective); absent on intra-MCQ fields, where
    // Enter just commits. Mid-text Enter (caret not at end) still commits — a
    // true split is deferred (document-first-editor.md §4).
    readonly onEnterAtEnd?: (committedText: string) => void;
    // The document-first "delete this empty block, focus the previous" gesture:
    // Backspace pressed while the text is EMPTY. Wired only on top-level document
    // blocks; absent elsewhere. A non-empty block's Backspace edits text as
    // normal (a text-merge into the previous block is deferred, §4).
    readonly onBackspaceEmpty?: () => void;
    // Enter EDITING mode (with focus) the moment THIS instance mounts,
    // instead of the resting display face — the block-editor "keep typing
    // after `# `, after a slash-insert" continuity fix (Bug 1, 2026-08-19).
    // A retype/insert always mounts a BRAND-NEW EditableText (a different
    // renderer takes over the same element id, or the element is new
    // outright) — there is no PREVIOUS instance whose `editing`/`draft`
    // state could carry over, so this seeds the new one's INITIAL state
    // instead of trying to preserve anything across the swap. Read ONLY at
    // mount (`useState`'s initializer runs once); a later prop flip does
    // NOT retroactively re-open editing — see `onAutoEditConsumed`.
    readonly autoEdit?: boolean;
    // Fired once, right after mount, when `autoEdit` was true — lets the
    // host clear its one-shot signal so a LATER remount of this same
    // element (a second retype) needs an explicit new signal rather than
    // spuriously re-firing off a stale one.
    readonly onAutoEditConsumed?: () => void;
};

export const EditableText = ({
    value,
    onCommit,
    ariaLabel,
    placeholder,
    renderDisplay,
    className,
    rules,
    onRuleMatch,
    slashTriggerState,
    onSlashTrigger,
    onSlashNavigate,
    onSlashCommitActive,
    onEnterAtEnd,
    onBackspaceEmpty,
    autoEdit,
    onAutoEditConsumed,
}: EditableTextProps) => {
    const [editing, setEditing] = useState(autoEdit === true);
    const [draft, setDraft] = useState(value);
    // Guards a double-commit: pressing Enter commits AND unmounts the textarea,
    // whose blur would otherwise commit a second time before `value` catches up.
    const doneRef = useRef(false);

    // Consume the mount-time `autoEdit` signal exactly once. Intentionally
    // depends on nothing (mirrors the `useState(autoEdit)` initializer
    // above, which also reads `autoEdit` ONLY at mount) — a prop change on
    // an already-mounted instance must NOT re-fire this; only a fresh mount
    // (a new `autoEdit={true}`) does, and that is a new effect instance by
    // construction.
    // biome-ignore lint/correctness/useExhaustiveDependencies: mount-once by design — autoEdit is read at mount only, exactly like the useState initializer above.
    useEffect(() => {
        if (autoEdit === true) onAutoEditConsumed?.();
    }, []);

    const enter = () => {
        doneRef.current = false;
        setDraft(value);
        setEditing(true);
    };
    const commit = () => {
        if (doneRef.current) return;
        doneRef.current = true;
        setEditing(false);
        if (draft !== value) onCommit(draft);
    };
    const cancel = () => {
        doneRef.current = true;
        setEditing(false);
        setDraft(value);
    };

    const cls = `bilda-editable-text${className ? ` ${className}` : ""}`;

    // The query typed after a leading `/` — empty once the text is not a bare
    // slash-prefix (mirrors `isSlashPrefix` in inputRules.ts; recomputed here
    // rather than imported since it is a one-line derivation of `text`, not a
    // decision this leaf owns).
    const slashQuery = (text: string): string =>
        text.startsWith("/") ? text.slice(1) : "";

    if (editing)
        return (
            <textarea
                // autoFocus is intentional: the textarea REPLACES the affordance
                // the author just activated, so focus must follow it there —
                // without it a keyboard user is dropped back to the page.
                // biome-ignore lint/a11y/noAutofocus: focus continuity on swap.
                autoFocus
                rows={1}
                className={`${cls} bilda-editable-text--input`}
                aria-label={ariaLabel}
                placeholder={placeholder}
                value={draft}
                onChange={(event) => {
                    const next = event.target.value;
                    setDraft(next);
                    // Live "as you type" detection — SHORTCUT (lifecycle-design:
                    // stated explicitly): only checked while the caret sits at
                    // the END of the text, the normal forward-typing case. A
                    // caret placed mid-text (a paste, an edit further back)
                    // never fires a rule/slash-trigger — this avoids retyping
                    // or opening the menu off a prefix that merely appears
                    // earlier in already-typed content.
                    const caretAtEnd =
                        event.target.selectionStart === next.length;
                    if (caretAtEnd && rules !== undefined) {
                        const match = matchRule(rules, next);
                        if (match !== undefined) onRuleMatch?.(match);
                    }
                    if (caretAtEnd && onSlashTrigger !== undefined) {
                        onSlashTrigger(
                            nextSlashTrigger(
                                slashTriggerState ?? "closed",
                                next,
                            ),
                            slashQuery(next),
                        );
                    }
                }}
                onBlur={commit}
                onKeyDown={(event) => {
                    const field = event.currentTarget;
                    const slashOpen = slashTriggerState === "open";
                    // --- Slash-menu keyboard nav (only while the menu is open) ---
                    // ↑/↓ move the menu cursor; the caret does not move. When the
                    // menu is closed these fall through to the textarea's native
                    // line movement (no preventDefault).
                    if (
                        slashOpen &&
                        (event.key === "ArrowDown" ||
                            event.key === "ArrowUp") &&
                        onSlashNavigate !== undefined
                    ) {
                        event.preventDefault();
                        onSlashNavigate(
                            event.key === "ArrowDown" ? "down" : "up",
                        );
                        return;
                    }
                    // Enter: pick the slash row if the menu is open; else the
                    // document-first "new block below" at end-of-text; else the
                    // plain text commit. Shift+Enter is always a newline (real
                    // breaks in a wrapping objective / paragraph).
                    if (event.key === "Enter" && !event.shiftKey) {
                        event.preventDefault();
                        if (slashOpen && onSlashCommitActive !== undefined) {
                            // The "/query" was a command trigger, not content:
                            // DISCARD the draft (guard the ensuing blur so it
                            // can't commit "/" as this block's text) before the
                            // host inserts the picked block.
                            doneRef.current = true;
                            setEditing(false);
                            onSlashCommitActive();
                            return;
                        }
                        const caretAtEnd =
                            field.selectionStart === draft.length &&
                            field.selectionEnd === draft.length;
                        if (onEnterAtEnd !== undefined && caretAtEnd) {
                            // Hand the text to the host, which persists this
                            // edit AND the sibling insert in one sequenced
                            // chain. Guard the pending blur so it does not
                            // double-commit the same text behind the host's back.
                            doneRef.current = true;
                            setEditing(false);
                            onEnterAtEnd(draft);
                            return;
                        }
                        commit();
                        return;
                    }
                    if (event.key === "Escape") {
                        event.preventDefault();
                        // An OPEN slash menu takes the first Escape (dismiss the
                        // menu, keep editing); a second Escape (menu already
                        // dismissed/closed) reverts the edit as before.
                        if (slashOpen && onSlashTrigger !== undefined) {
                            onSlashTrigger(
                                dismissSlashTrigger(),
                                slashQuery(draft),
                            );
                            return;
                        }
                        cancel();
                        return;
                    }
                    // Backspace on an EMPTY block ⇒ delete it + focus previous
                    // (merge of text is deferred). A non-empty block edits text.
                    if (
                        event.key === "Backspace" &&
                        onBackspaceEmpty !== undefined &&
                        draft === ""
                    ) {
                        event.preventDefault();
                        doneRef.current = true;
                        setEditing(false);
                        onBackspaceEmpty();
                        return;
                    }
                }}
            />
        );

    // Empty value + a placeholder ⇒ show the dimmed placeholder as the clickable
    // "Add …" target (a redundant modifier class carries the dim, colour is not
    // the only channel — the placeholder WORDS say it is empty).
    const showPlaceholder = value === "" && placeholder !== undefined;
    return (
        // A native button: keyboard-operable for free (Enter/Space), named by
        // `ariaLabel`, styled (see .css) to read as the plain text it edits.
        <button
            type="button"
            className={`${cls} bilda-editable-text--display${
                showPlaceholder ? " bilda-editable-text--placeholder" : ""
            }`}
            aria-label={ariaLabel}
            onClick={enter}
        >
            {showPlaceholder
                ? placeholder
                : renderDisplay
                  ? renderDisplay(value)
                  : value}
        </button>
    );
};
